import React, { Dispatch, SetStateAction, useCallback } from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Button, Stack } from "@mui/material";
import { PsychologyReverse } from "assets/material-custom-icons";
import { useRoleGuardHook } from "hooks";
import { useDealActions, useDocumentsActions, usePickUserPermissions } from "store";
import { DocumentQuestion, DocumentSectionAIFile, UserPermissionType } from "store/types";

import { RoleName } from "../../../constants";
import { useContentContext } from "../Content/content-context";
import { FileBlock } from "../FileBlock/FileBlock";
import { QuestionsSet } from "../Questions/QuestionsSet";

interface Props {
  section: DocumentSectionAIFile | null;
  onDelete: () => void;
  onFocus?: () => void;
  className?: string;
  setPendingAIQuestionIds: Dispatch<SetStateAction<number[]>>;
  pendingAIQuestionIds: number[];
  questions: DocumentQuestion[];
}

export const AIFileSection: React.FC<Props> = ({
  section,
  onDelete,
  onFocus,
  setPendingAIQuestionIds,
  pendingAIQuestionIds,
  questions
}) => {
  const { downloadDocumentFileAction } = useDocumentsActions();

  const { createQuestion, deleteQuestion } = useContentContext();

  if (!section) return;

  const { document_id, id: sectionId, file } = section;

  // const questions = useDocumentQuestionsSelect({ documentId: document_id, section_id: sectionId });
  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;

  const { downloadFileAction } = useDealActions();

  const enabled = useRoleGuardHook(true, [RoleName.Admin, RoleName.Analyst]);

  const onDownload = () => {
    if (!file) return;
    if (file.deal_id) {
      downloadFileAction({ fileId: file.id, fileName: file.name, dealId: file.deal_id });
    } else {
      file.document_id && downloadDocumentFileAction({ documentId: file.document_id, fileId: file.id });
    }
  };

  const handleCreateQuestion = useCallback(() => {
    return createQuestion({
      section_id: sectionId,
      documentId: document_id
    });
  }, [sectionId, document_id, createQuestion]);

  return (
    <Stack
      onFocus={onFocus}
      tabIndex={0}
      sx={{
        gap: "10px",
        p: "10px",
        mb: "1.5px",
        boxShadow: "0px 0px 3px 0px rgba(0, 0, 0, 0.25)",
        borderRadius: "5px",
        flexGrow: 1
      }}
    >
      <Stack direction="row" sx={{ gap: "10px", alignItems: "center" }}>
        <Button
          disabled={section?.ai_status === "in-progress" || !!noAccessPermission || !!isViewerPermission}
          onClick={handleCreateQuestion}
          sx={{
            padding: "1px 10px",
            minWidth: "auto",
            whiteSpace: "nowrap",
            background: "#DBE8F5",
            fontSize: "12px",
            "&:hover": {
              opacity: 0.8
            }
          }}
          variant="text"
          startIcon={<PsychologyReverse sx={{ fontSize: "16px" }} />}
        >
          Ask AI
        </Button>
        {section.file && (
          <FileBlock
            file={section.file}
            menuOptions={[
              {
                title: "Download",
                icon: <FileDownloadOutlinedIcon sx={{ color: "#0a3142" }} fontSize="md" />,
                onClick: onDownload
              },
              {
                title: "Delete",
                icon: <DeleteOutlineIcon sx={{ color: "#0a3142" }} fontSize="md" />,
                onClick: onDelete,
                hidden: !enabled || !!noAccessPermission || !!isViewerPermission
              }
            ]}
          />
        )}
      </Stack>
      {Boolean(questions.length) && (
        <QuestionsSet
          documentId={document_id}
          questions={questions}
          createQuestion={handleCreateQuestion}
          deleteQuestion={deleteQuestion}
          setPendingAIQuestionIds={setPendingAIQuestionIds}
          pendingAIQuestionIds={pendingAIQuestionIds}
          hideAddQuestion={!!noAccessPermission || !!isViewerPermission}
          section={section}
        />
      )}
    </Stack>
  );
};
